import { useServices } from '@/app/providers/ServiceContext';
import type { User } from '@/shared/types';
import type { AbilityUser, AppAbility } from '@yawi/casl';
import { AppAbilityClass, defineAbilitiesFor } from '@yawi/casl';
import type React from 'react';
import { createContext, createElement, useContext, useMemo } from 'react';

// Re-export types from package for convenience
export { canDo, defineAbilitiesFor, getAllowedActions, Subjects } from '@yawi/casl';
export type { AbilityUser, Action, AppAbility } from '@yawi/casl';

// Permission type for menu items and routes
export interface Permission {
  action: 'create' | 'read' | 'update' | 'delete' | 'manage';
  subject: string;
}

// React Context for abilities
export const AbilityContext = createContext<AppAbility>(new AppAbilityClass());

export const useAbility = () => useContext(AbilityContext);

/**
 * Admin acotado a su sección (§25). Opera el panel como un ADMIN; el backend
 * limita cada registro a su sección.
 */
export const isSectionAdmin = (user: Pick<User, 'role' | 'tenantRole'> | null | undefined): boolean =>
  String(user?.tenantRole || '').toUpperCase() === 'SECTION_ADMIN'
  || String(user?.role || '').toUpperCase() === 'SECTION_ADMIN';

// Convert User to AbilityUser
function toAbilityUser(user: User | null): AbilityUser | null {
  if (!user) return null;

  // Mapear el rol del usuario al formato esperado por CASL
  // tenantRole tiene prioridad, luego normalizar TENANT_USER a RESIDENT
  let effectiveRole: 'ADMIN' | 'RESIDENT' | 'GUARD' | 'FAMILIAR' | 'SECTION_ADMIN' | 'CUSTOM' = 'RESIDENT';
  const tenantRole = String(user.tenantRole || '').toUpperCase();
  const role = String(user.role || '').toUpperCase();

  switch (tenantRole) {
    case 'ADMIN':
    case 'TENANT_ADMIN':
      effectiveRole = 'ADMIN';
      break;
    case 'SECTION_ADMIN':
      effectiveRole = 'SECTION_ADMIN';
      break;
    case 'GUARD':
      effectiveRole = 'GUARD';
      break;
    case 'FAMILIAR':
      effectiveRole = 'FAMILIAR';
      break;
    case 'RESIDENT':
    case 'TENANT_USER':
      effectiveRole = 'RESIDENT';
      break;
    default:
      if (tenantRole) {
        effectiveRole = 'CUSTOM';
      }
      break;
  }

  if (!tenantRole) {
    switch (role) {
      case 'ADMIN':
      case 'TENANT_ADMIN':
        effectiveRole = 'ADMIN';
        break;
      case 'GUARD':
        effectiveRole = 'GUARD';
        break;
      case 'SECTION_ADMIN':
        effectiveRole = 'SECTION_ADMIN';
        break;
      default:
        break;
    }
  }

  // Obtener unitId de la nueva estructura o legacy
  let unitId: string | undefined;
  if (user.units && user.units.length > 0) {
    const primary = user.units.find(u => u.isPrimary) || user.units[0];
    unitId = primary.unitId;
  } else if (user.residenceInfo) {
    unitId = `${user.residenceInfo.tower}-${user.residenceInfo.unit}`;
  }

  return {
    id: user.id,
    role: effectiveRole,
    abilities: user.abilities || [],
    unitId,
  };
}

export const AbilityProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentUser } = useServices();

  const ability = useMemo(() => {
    return defineAbilitiesFor(toAbilityUser(currentUser));
  }, [currentUser]);

  return createElement(AbilityContext.Provider, { value: ability }, children);
};
